import { forwardRef } from "react";
import { Label } from "../Label/Label";
import { SelectProps } from "../types";
import { findError } from "../util";
import { FormFieldError } from "../FormFieldError/FormFieldError";
import { BASE_INPUT_COLORS, SIZE_MAP, STYLE_MAP } from "../styles";
import { FormFieldWrapper } from "../FormFieldWrapper";
import {
  FieldValues,
  UseFormGetValues,
  UseFormSetValue
} from "react-hook-form";
import { MultiSelect, Option } from "react-multi-select-component";

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      label,
      errors,
      description,
      descriptionType,
      selectOptions,
      clearable = false,
      noOptionsSelectedLabel = "Select",
      inputSize = "regular",
      className = "mb-5",
      helperText,
      mode = "light",
      variant = "filled",
      disabled = false,
      ...inputProps
    },
    ref
  ) => {
    const { name } = inputProps;

    const error = findError(name, errors);

    return (
      <div className={className}>
        <Label
          label={label}
          name={name}
          description={description}
          descriptionType={descriptionType}
          mode={mode}
        />
        <select
          className={`${STYLE_MAP[mode][variant].primary} ${BASE_INPUT_COLORS} ${SIZE_MAP[inputSize]} block  w-full rounded align-left pr-8 truncate`}
          id={inputProps.name}
          ref={ref}
          disabled={disabled}
          defaultValue={""}
          {...inputProps}
        >
          <option value="" disabled={!clearable}>
            {noOptionsSelectedLabel}
          </option>
          {selectOptions.map(({ value, label }) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        {helperText && (
          <p className={`${STYLE_MAP[mode]["text"].secondary} text-xs mt-1 `}>
            {helperText}
          </p>
        )}
        {error && (
          <FormFieldError error={error.message?.toString() || "required"} />
        )}
      </div>
    );
  }
);

type MultiSelectFieldProps<T extends FieldValues> = SelectProps & {
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
};
export const MultiSelectField = forwardRef<
  HTMLSelectElement,
  MultiSelectFieldProps<any>
>(
  (
    {
      label,
      errors,
      description,
      descriptionType,
      selectOptions,
      clearable = false,
      noOptionsSelectedLabel = "Select",
      className = "",
      helperText,
      mode = "light",
      variant = "filled",
      getValues,
      setValue,
      ...inputProps
    },
    ref
  ) => {
    const { name } = inputProps;

    const options = selectOptions.map((o) => {
      return {
        label: o.label!,
        value: o.value!
      };
    });

    return (
      <FormFieldWrapper
        {...{
          name,
          errors,
          label,
          description,
          descriptionType,
          helperText,
          mode
        }}
      >
        <MultiSelect
          className={`mb-0 w-full`}
          options={options}
          labelledBy={name}
          value={(getValues("table").length > 0 ? getValues("table") : []).map(
            (v: string) => {
              return {
                label: v,
                value: v
              };
            }
          )}
          onChange={(chosen: Option[]) => {
            setValue(
              "table",
              chosen.map((o) => o.value),
              { shouldValidate: true, shouldTouch: true }
            );
          }}
        />
      </FormFieldWrapper>
    );
  }
);
